import React, { useEffect, useState } from 'react';
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  persistent?: boolean;
  logoSrc?: string; // Configurable logo source
  onDismiss: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  persistent = false,
  logoSrc = '/logo.png', // Default placeholder logo
  onDismiss
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [progress, setProgress] = useState(100);
  const [isPaused, setIsPaused] = useState(false);
  const [, setElapsedTime] = useState(0);

  useEffect(() => {
    // Trigger enter animation
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  useEffect(() => {
    // Auto dismiss with progress bar (skip if persistent)
    if (!persistent && duration > 0 && !isPaused) {
      const interval = setInterval(() => {
        setElapsedTime(prev => {
          const next = prev + 100;
          if (next >= duration) {
            handleDismiss();
            return duration;
          }
          return next;
        });
        setProgress(prev => {
          const next = prev - (100 / (duration / 100));
          if (next <= 0) {
            return 0;
          }
          return next;
        });
      }, 100);

      return () => {
        clearInterval(interval);
      };
    }
  }, [duration, isPaused, persistent]);

  const handleMouseEnter = () => {
    setIsPaused(true);
    setProgress(100);
    setElapsedTime(0);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onDismiss(id);
    }, 300);
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-white/90 drop-shadow-md flex-shrink-0" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-white/90 drop-shadow-md flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-white/90 drop-shadow-md flex-shrink-0" />;
      case 'info':
        return <Info className="w-5 h-5 text-white/90 drop-shadow-md flex-shrink-0" />;
    }
  };

  // Theme-aware toast colors using CSS variables + color-mix for gradients
  const themeColors: Record<string, {
    from: string; to: string;       // main gradient
    logoFrom: string; logoTo: string; // logo panel gradient
    border: string;
    progress: string;
  }> = {
    success: {
      from: 'hsl(var(--header-background))',
      to: 'color-mix(in srgb, hsl(var(--header-background)), white 15%)',
      logoFrom: 'color-mix(in srgb, hsl(var(--header-background)), black 15%)',
      logoTo: 'hsl(var(--header-background))',
      border: 'hsl(var(--header-foreground) / 0.15)',
      progress: 'hsl(var(--header-foreground) / 0.4)',
    },
    error: {
      from: 'hsl(var(--destructive))',
      to: 'color-mix(in srgb, hsl(var(--destructive)), black 25%)',
      logoFrom: 'color-mix(in srgb, hsl(var(--destructive)), black 35%)',
      logoTo: 'color-mix(in srgb, hsl(var(--destructive)), black 15%)',
      border: 'hsl(var(--destructive) / 0.4)',
      progress: 'hsl(var(--destructive-foreground) / 0.5)',
    },
    warning: {
      from: 'hsl(38, 92%, 50%)',
      to: 'color-mix(in srgb, hsl(38, 92%, 50%), hsl(25, 90%, 40%) 50%)',
      logoFrom: 'color-mix(in srgb, hsl(38, 92%, 50%), black 30%)',
      logoTo: 'color-mix(in srgb, hsl(38, 92%, 50%), black 15%)',
      border: 'hsl(38, 92%, 50% / 0.3)',
      progress: 'hsl(45, 93%, 70%)',
    },
    info: {
      from: 'hsl(var(--ring))',
      to: 'color-mix(in srgb, hsl(var(--ring)), hsl(240, 60%, 40%) 40%)',
      logoFrom: 'color-mix(in srgb, hsl(var(--ring)), black 30%)',
      logoTo: 'color-mix(in srgb, hsl(var(--ring)), black 10%)',
      border: 'hsl(var(--ring) / 0.3)',
      progress: 'hsl(var(--ring) / 0.5)',
    },
  };

  const tc = themeColors[type as string] || themeColors.info;

  const getStyles = () => {
    const baseStyles = 'relative flex mb-3 rounded-xl shadow-2xl overflow-hidden backdrop-blur-sm transition-all duration-300 min-w-[380px] max-w-[480px] text-white';

    const animationStyles = isLeaving
      ? 'transform translate-x-full opacity-0 scale-95'
      : isVisible
        ? 'transform translate-x-0 opacity-100 scale-100'
        : 'transform translate-x-full opacity-0 scale-95';

    return `${baseStyles} ${animationStyles}`;
  };

  const getToastStyle = (): React.CSSProperties => ({
    background: `linear-gradient(to right, ${tc.from}, ${tc.to})`,
    borderWidth: '1px',
    borderColor: tc.border,
  });

  const getLogoStyle = (): React.CSSProperties => ({
    background: `linear-gradient(to bottom right, ${tc.logoFrom}, ${tc.logoTo})`,
  });

  const getProgressBarStyle = (): React.CSSProperties => ({
    backgroundColor: tc.progress,
  });

  return (
    <div
      className={getStyles()}
      style={getToastStyle()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Subtle diagonal stripe overlay across entire toast */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.07]" style={{
        backgroundImage: `repeating-linear-gradient(
          45deg,
          transparent,
          transparent 10px,
          rgba(255,255,255,0.3) 10px,
          rgba(255,255,255,0.3) 20px
        )`
      }} />

      {/* Logo Section */}
      <div className="flex items-center justify-center p-4 shadow-inner relative overflow-hidden" style={getLogoStyle()}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              rgba(255,255,255,0.1) 10px,
              rgba(255,255,255,0.1) 20px
            )`
          }} />
        </div>

        {/* Logo container - configurable logo with white filter */}
        <div className="relative flex items-center justify-center">
          <img
            src={logoSrc}
            alt="Logo"
            className="w-20 h-20 object-contain brightness-0 invert filter drop-shadow-xl"
            style={{
              filter: 'brightness(0) invert(1) drop-shadow(0 10px 20px rgba(0,0,0,0.3))'
            }}
          />
        </div>
      </div>

      {/* Content Section */}
      <div className="flex-1 p-5 pr-12">
        <div className="flex-1">
          <div className="flex items-center gap-2.5 pb-2 border-b border-white/30">
            {getIcon()}
            <h4 className="font-bold text-white text-base tracking-wide drop-shadow-sm">
              {title}
            </h4>
          </div>
          {message && (
            <p className="text-sm mt-2.5 text-white/90 leading-relaxed">
              {message}
            </p>
          )}
        </div>
      </div>

      {/* Progress Bar (only show if not persistent) */}
      {!persistent && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
          <div
            className={`h-full ${isPaused ? 'transition-all duration-300 ease-out' : 'transition-all duration-100 ease-linear'}`}
            style={{ width: `${progress}%`, ...getProgressBarStyle() }}
          />
        </div>
      )}

      {/* Close Button */}
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors hover:rotate-90 transform duration-200"
        aria-label="Dismiss notification"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

export default Toast;
